import axios from "axios";
import { getToken, isTokenExpired } from "./persist";
import getEnvEndpoints from "./endpoints";
import { handleTokenRefreshFailure, refreshToken } from "../components/auth/tokenRefreshService";

const endpoints = getEnvEndpoints();

const axiosInstance = axios.create({
  baseURL: endpoints.baseApiUrl
});

const parseErrors = err => {
  console.log(err.response);
  if (err.response) {
    switch (err.response.status) {
      case 401:
        const path = `${window.location.pathname}${window.location.hash}`;
        window.location = `/login?redirect=${path}`;
        break;
      case 403:
        window.location = `/`;
        break;
      case 404:
        window.location = `/404`;
        break;
      default:
        throw err.response.data;
    }
  } else if (err.request) {
    console.log(err, "network error");
  } else {
    console.log("something unique happened");
  }
};

// Refreshes an expired access token before the call; returns null when the session can't be renewed.
const createHeaders = async (additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders
  };

  let token = getToken();
  if (token && isTokenExpired(token)) {
    token = await refreshToken();
    if (!token) {
      handleTokenRefreshFailure();
      return null;
    }
  }

  if (token) {
    headers.Authorization = "Bearer " + token;
  }

  return headers;
};

// Signed-in 401s refresh the token and retry once; anonymous 401s still go to /login via parseErrors.
const sendAuthorized = async (send, additionalHeaders = {}, isRetry = false) => {
  const headers = await createHeaders(additionalHeaders);
  if (!headers) return undefined;

  try {
    const res = await send(headers);
    return res.data;
  } catch (err) {
    if (err && err.response && err.response.status === 401 && getToken()) {
      if (!isRetry) {
        const newToken = await refreshToken();
        if (newToken) {
          return sendAuthorized(send, additionalHeaders, true);
        }
        handleTokenRefreshFailure();
      } else {
        // A freshly refreshed token was still rejected; stop instead of looping back through the API.
        handleTokenRefreshFailure("The StandardsPlus API rejected your access token (401).");
      }
      return undefined;
    }
    return parseErrors(err);
  }
};

export const makeRequest = async (
  method,
  url,
  data = {},
  additionalHeaders = {}
) =>
  sendAuthorized(
    headers => axiosInstance({ method, url, data, headers }),
    additionalHeaders
  );

export const fetcher = async (path, method = "get") =>
  sendAuthorized(headers => axiosInstance[method](path, { headers }));

export const postsFetcher = async (path, data, options = {}) =>
  sendAuthorized(headers => axiosInstance.post(path, data, { ...options, headers }));

export const logsFetcher = async (path, logType) =>
  sendAuthorized(headers =>
    axiosInstance.post(
      path,
      {
        logType: logType
      },
      { headers }
    )
  );

export const commentsFetcher = async (path, commentType) =>
  sendAuthorized(headers =>
    axiosInstance.post(
      path,
      {
        commentType: commentType
      },
      { headers }
    )
  );

export const anonymousFetcher = async path => {
  try {
    const res = await axiosInstance.get(path);
    return res.data;
  } catch (err) {
    parseErrors(err);
  }
};
